import React, { useState } from 'react';
import { matchPath } from 'react-router-dom';
import { uniqueId } from 'lodash';
import classNames from 'classnames';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import LinkButton from 'components/shared/LinkButton';
import LoadingSkeleton from './LoadingSkeleton';

const BTN_CLASSES =
  'NewButton NewButton--small NewButton--center Modal-button ContentHelperModal-button';

const match = () =>
  matchPath(window.location.pathname, { path: '/lessons/:lessonId' });

const getCorrectAnswer = choices => {
  const correctChoices = [];
  choices.split('\n').forEach(choice => {
    if (choice.includes('*')) {
      correctChoices.push(choice.replace('*', ''));
    }
  });
  return correctChoices;
};

const MultipleChoiceBody = ({
  content: { subject, raw_choices: rawChoices, question_type: questionType },
  dismissCallback,
  setError,
  applyChanges,
  handleUpdate,
}) => {
  const providedCorrectChoice = getCorrectAnswer(rawChoices);
  const [question, setQuestion] = useState(subject);
  const [correctChoice, setCorrectChoice] = useState(providedCorrectChoice);
  const [incorrectChoices, setIncorrectChoices] = useState([]);
  const [numberIncorrectChoices, setNumberIncorrectChoices] = useState(2);
  const initialNumCorrectChoices = questionType === 'multiple_select' ? 2 : 1;
  const [numberCorrectChoices, setNumberCorrectChoices] = useState(
    initialNumCorrectChoices,
  );
  const [isGenerated, setIsGenerated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const lessonId = match()?.params?.lessonId.split('-')[0];
  const unformatAnswers = answers => {
    const correctAnswer = [];
    const incorrectAnswer = [];
    answers.forEach(answer => {
      const { answer: choice, correct } = answer;
      if (correct) {
        correctAnswer.push(choice);
      } else {
        incorrectAnswer.push(choice);
      }
    });
    return { correctAnswer, incorrectAnswer };
  };

  const formatAnswers = () => {
    let formattedChoices = '';
    correctChoice.forEach(choice => {
      formattedChoices += `${choice}*\n`;
    });
    incorrectChoices.forEach((choice, index, arr) => {
      // don't add a new line on the last choice
      if (index === arr.length - 1) {
        formattedChoices += choice;
      } else {
        formattedChoices += `${choice}\n`;
      }
    });
    return formattedChoices;
  };

  const accept = () => {
    applyChanges({
      subject: question,
      raw_choices: formatAnswers(),
    });
    handleUpdate({
      subject: question,
      raw_choices: formatAnswers(),
    });
    dismissCallback();
  };

  const generate = async () => {
    setError(false);
    setIsLoading(true);
    request
      .get(
        routes.ELEMENT_HELPER.MULTIPLE_CHOICE(
          lessonId,
          numberIncorrectChoices,
          numberCorrectChoices,
          question,
          correctChoice,
        ),
      )
      .then(data => {
        const { content } = data.response.choices[0].message;
        const { prompt, answers } = JSON.parse(content);
        const { correctAnswer, incorrectAnswer } = unformatAnswers(answers);
        if (!subject) setQuestion(prompt);
        if (providedCorrectChoice.length === 0) setCorrectChoice(correctAnswer);
        setIncorrectChoices(incorrectAnswer);
        setIsGenerated(true);
        setIsLoading(false);
      })
      .catch(e => {
        setError(true);
        setIsLoading(false);
      });
  };

  return (
    <>
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <div className="ContentHelperModal-input MultipleChoice">
          <div className="ContentHelperModal-block">
            <div>
              {t(
                'questions.question.element_helper.select_option.number_incorrect_choices',
              )}
            </div>
            <input
              className="NewInput NewInput--white mt-8"
              type="number"
              onChange={e => setNumberIncorrectChoices(e.target.value)}
              value={numberIncorrectChoices}
            />
          </div>
          {correctChoice.length === 0 && questionType === 'multiple_select' && (
            <div className="ContentHelperModal-block">
              <div>
                {t(
                  'questions.question.element_helper.select_option.correct_choices',
                )}
              </div>
              <input
                className="NewInput NewInput--white mt-8"
                type="number"
                onChange={e => setNumberCorrectChoices(e.target.value)}
                value={numberCorrectChoices}
              />
            </div>
          )}
          {question && (
            <div className="ContentHelperModal-block">
              <div>
                {t('questions.question.element_helper.select_option.question')}{' '}
                {question}
              </div>
            </div>
          )}
          {correctChoice.length > 0 && (
            <div className="ContentHelperModal-block">
              <div>
                {t('questions.question.element_helper.select_option.correct')}{' '}
                {correctChoice.legth === 1 ? (
                  correctChoice
                ) : (
                  <ul>
                    {correctChoice.map(choice => (
                      <li key={uniqueId()}>{choice}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          {incorrectChoices.length > 0 && (
            <div className="ContentHelperModal-block">
              <div>
                {t('questions.question.element_helper.select_option.incorrect')}
              </div>
              <ul>
                {incorrectChoices.map(incorrectChoice => (
                  <li key={uniqueId()}>{incorrectChoice}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <div className="ContentHelperModal--actions d-f ai-c jc-e">
        <LinkButton className="cancelButton" onClick={dismissCallback}>
          {t('saved_content.button.cancel')}
        </LinkButton>
        <button
          type="button"
          className={classNames(`${BTN_CLASSES} NewButton--blue`, {
            'NewButton--disabled': isLoading,
          })}
          onClick={generate}
          disabled={isLoading}
        >
          {isGenerated
            ? t('questions.question.element_helper.regenerate')
            : t('questions.question.element_helper.generate')}
        </button>
        {isGenerated && (
          <button
            type="button"
            className={`${BTN_CLASSES} NewButton--success`}
            onClick={accept}
            disabled={isLoading}
          >
            {t('questions.question.element_helper.accept')}
          </button>
        )}
      </div>
    </>
  );
};

export default MultipleChoiceBody;
